import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {BusinessUserInvite, InviteData} from '../../common/types'
import {SignInForm, SignUpForm} from '../../components/auth'
import {ConfirmAccountForm} from '../../components/auth/confirm'
import {
  AuthButtons,
  AuthLargeHeader,
  AuthLogo,
  AuthMessage
} from '../../components/auth/layout'
import {AuthComplete} from '../../components/auth/types'
import {FormRow} from '../../components/form'
import {Button} from '../../components/library/buttons/index'
import {TextInput} from '../../components/library/form'
import {Loader} from '../../components/loader'
import {useActivateInvitedBusinessUserAccountMutation} from '../../data/workbench-api/generated/organisation-invite'
import {isMFAError} from '../../is-mfa-error'
import {useAnalytics} from '../../lib/analytics/analytics'
import {getClient} from '../../lib/api'
import {useAuth} from '../../lib/auth'
import {Authenticator, AuthState, AuthStateAuth} from '../../lib/cognito'
import {BU_COGNITO_APP_ID, ROUTING_API_URL} from '../../lib/env'
import {coerceString} from '../../lib/helpers'
import {useFocusFirstFormInput} from '../../lib/hooks'
import {useNode} from '../../lib/jsonAPI'
import {getLocalOrganisationId} from '../../lib/local-store'
import {useOrganisations} from '../../lib/organisations'
import {useToast} from '../../lib/toast'
import {MfaSetup} from '../profile/mfa'
import {AuthLayout} from './layout'

interface AcceptInvitationParams {
  secretValue: string
}

export const AcceptInvitation = ({secretValue}: AcceptInvitationParams) => {
  const {auth, authState} = useAuth()

  const {node, loading: inviteLoading} = useNode<BusinessUserInvite>(
    `${ROUTING_API_URL}/invite/${secretValue}`,
    undefined,
    {authIsOptional: true}
  )
  const businessUserInvite = node ? node.attributes : undefined

  const loading = inviteLoading || !authState.loaded

  return (
    <AuthLayout>
      {loading ? (
        <Loader variant="inline" />
      ) : businessUserInvite ? (
        <InvitationContent
          businessUserInvite={businessUserInvite}
          auth={auth}
          authState={authState}
          secretValue={secretValue}
        />
      ) : (
        <InviteError />
      )}
    </AuthLayout>
  )
}

interface InvitationContentProps {
  businessUserInvite: BusinessUserInvite['attributes']
  auth: Authenticator
  authState: AuthState
  secretValue: string
}

const InvitationContent = ({
  businessUserInvite,
  auth,
  authState,
  secretValue
}: InvitationContentProps) => {
  const navigate = useNavigate()
  const analytics = useAnalytics()
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false)
  const [, {refetch: refetchOrgs}] = useOrganisations()
  const toaster = useToast()
  const organisationId = businessUserInvite.organisationId

  const useAcceptInvite = useActivateInvitedBusinessUserAccountMutation

  const [acceptInviteMutation, {loading: acceptInviteLoading}] =
    useAcceptInvite({
      variables: {
        secretValue,
        organisationId
      },
      onError: error => {
        if (isMFAError(error)) {
          setMfaSetupRequired(true)
        }
      },
      onCompleted: async res => {
        await auth.refreshAuthState()
        await refetchOrgs()
        const user = res.activateInvitedBusinessUserAccount?.businessUser
        analytics.identify({
          traits: {
            org_id: organisationId,
            org_name: businessUserInvite.organisationName,
            // In reality these will also be set
            // - graphile marks them as nullable ( as it can't ensure RLS isn't hiding user )
            first_name: user?.firstName ?? undefined,
            last_name: user?.lastName ?? undefined,
            email: user?.email ?? undefined
          }
        })
        analytics.track({
          event: 'Joined org',
          properties: {
            category: 'general',
            org_id: businessUserInvite.organisationId,
            org_name: businessUserInvite.organisationName,
            first_name: coerceString(user?.firstName) ?? '',
            last_name: coerceString(user?.lastName) ?? ''
          }
        })
        setMfaSetupRequired(false)
        toaster(`Invite accepted`)
        navigate(`/${organisationId}`)
      },
      client: getClient(organisationId)
    })

  const [mode, setMode] = useState<'signup' | 'login' | 'confirm'>(
    businessUserInvite.ssoEnabled ? 'login' : 'signup'
  )
  const [credentials, setCredentials] = useState<{
    username: string
    password: string
  }>()
  const [inviteData, setInviteData] = useState<InviteData>()

  // https://stackoverflow.com/questions/2140627/how-to-do-case-insensitive-string-comparison
  // localeCompare provides àccent check, so prefer to toLowerCase()
  const emailMatch = authState.authenticated
    ? authState.email.localeCompare(businessUserInvite.email, undefined, {
        sensitivity: 'accent'
      }) === 0
    : false

  const requireCustomAppClient =
    businessUserInvite.appClientIds && !businessUserInvite.allowDefaultAppClient

  const currentAppClientAllowed =
    businessUserInvite.appClientIds.includes(auth.getClientId()) ||
    (businessUserInvite.allowDefaultAppClient &&
      auth.getClientId() === BU_COGNITO_APP_ID)

  const onAuthComplete: AuthComplete = async (
    state,
    usernamePassword,
    userData
  ) => {
    setCredentials(usernamePassword)
    setInviteData(userData)

    if (state === 'confirm') {
      setMode('confirm')
    } else {
      await acceptInviteMutation({
        variables: {
          ...(userData ? userData : {}),
          organisationId,
          secretValue
        }
      })
    }
  }

  useEffect(() => {
    sessionStorage.setItem('sso_success_redirect_url', `/signup/${secretValue}`)
  }, [secretValue])

  useEffect(() => {
    // User has signed up, and has confirmed account
    // now automatically join the organisation
    if (
      mode === 'confirm' &&
      authState.authenticated &&
      emailMatch &&
      currentAppClientAllowed
    ) {
      acceptInviteMutation({
        variables: {
          ...(inviteData ? inviteData : {}),
          secretValue,
          organisationId
        }
      })
    }
  }, [
    acceptInviteMutation,
    authState.authenticated,
    currentAppClientAllowed,
    emailMatch,
    mode,
    secretValue,
    inviteData,
    organisationId
  ])

  if (authState.authenticated && mfaSetupRequired) {
    return (
      <MfaSetup
        title="This organisation requires multi-factor authentication."
        authState={authState}
        auth={auth}
        checkPassword={false}
        cancelLink={false}
        onComplete={async () => {
          await auth.refreshAuthState()
          await acceptInviteMutation({
            variables: {
              ...(inviteData ? inviteData : {}),
              secretValue,
              organisationId
            }
          })
        }}
      />
    )
  }

  if (!authState.authenticated && mode === 'confirm' && credentials) {
    return (
      <>
        <ConfirmAccountForm
          username={credentials.username}
          onComplete={async () => {
            await auth.signIn(credentials.username, credentials.password)
            // Note once we are signed in the use effect to acceptInviteMutation will fire
            // so no need to call it here
          }}
        />
      </>
    )
  }

  return (
    <>
      {authState.authenticated ? (
        emailMatch && currentAppClientAllowed ? (
          // User is already logged in ( member of another org ), they need to join this org
          mode !== 'confirm' ? (
            <InviteAccept
              businessUserInvite={businessUserInvite}
              acceptInviteLoading={acceptInviteLoading}
              onAccept={async userData => {
                setInviteData(userData) // store in case acceptInviteMutation fails due to MFA etc
                await acceptInviteMutation({
                  variables: {
                    ...userData,
                    secretValue,
                    organisationId
                  }
                })
              }}
              authState={authState}
            />
          ) : (
            // show a loader while the useEffect above is firing, this is a newly confirmed user
            // the use effect is going add them to this organisation automatically then redirect
            <Loader variant="inline" />
          )
        ) : (
          <InviteAcceptMismatch
            businessUserInvite={businessUserInvite}
            authState={authState}
            auth={auth}
            emailMatch={emailMatch}
          />
        )
      ) : (
        <>
          {mode === 'signup' && !requireCustomAppClient ? (
            <InviteSignup
              businessUserInvite={businessUserInvite}
              switchToSignin={() => setMode('login')}
              onAuthComplete={onAuthComplete}
              auth={auth}
            />
          ) : (
            <InviteSignin
              businessUserInvite={businessUserInvite}
              switchToSignup={() => setMode('signup')}
              onAuthComplete={onAuthComplete}
              auth={auth}
            />
          )}
        </>
      )}
    </>
  )
}

interface InviteAcceptProps {
  businessUserInvite: BusinessUserInvite['attributes']
  authState: AuthStateAuth
  acceptInviteLoading: boolean
  onAccept: (data?: InviteData) => Promise<unknown>
}
const InviteAccept = ({
  businessUserInvite,
  authState,
  acceptInviteLoading,
  onAccept
}: InviteAcceptProps) => {
  const formRef = useFocusFirstFormInput()
  const [formData, setFormData] = useState<InviteData>(() => ({
    firstName: null,
    lastName: null,
    ...(businessUserInvite.inviteData ? businessUserInvite.inviteData : {})
  }))
  return (
    <>
      <AuthLogo />
      <AuthLargeHeader>Accept invite</AuthLargeHeader>
      <AuthMessage>You're currently logged in as {authState.email}</AuthMessage>

      <form
        style={{width: '100%'}}
        onSubmit={async e => {
          e.preventDefault()
          await onAccept(formData)
        }}
        ref={formRef}
      >
        <>
          {businessUserInvite.inviteData && ( // Legacy invites won't have inviteData
            <>
              <FormRow>
                <TextInput
                  type="text"
                  label="First name"
                  required={true}
                  onChange={firstName => {
                    setFormData(s => ({...s, firstName}))
                  }}
                  value={formData.firstName ?? ''}
                  style={{maxWidth: 'none'}}
                />
              </FormRow>
              <FormRow>
                <TextInput
                  type="text"
                  label="Last name"
                  required={true}
                  onChange={lastName => {
                    setFormData(s => ({...s, lastName}))
                  }}
                  value={formData.lastName ?? ''}
                  style={{maxWidth: 'none'}}
                />
              </FormRow>
            </>
          )}

          <AuthButtons>
            <Button type="submit" size="large" disabled={acceptInviteLoading}>
              Join {businessUserInvite.organisationName}
            </Button>
          </AuthButtons>
        </>
      </form>
    </>
  )
}

interface InviteAcceptMismatchProps {
  businessUserInvite: BusinessUserInvite['attributes']
  auth: Authenticator
  authState: AuthStateAuth
  emailMatch: boolean
}
const InviteAcceptMismatch = ({
  auth,
  authState,
  businessUserInvite,
  emailMatch
}: InviteAcceptMismatchProps) => {
  const analytics = useAnalytics()

  const requireDefaultAppClient =
    !businessUserInvite.appClientIds.length &&
    businessUserInvite.allowDefaultAppClient

  return (
    <>
      <AuthLogo />
      <AuthLargeHeader>Accept invite</AuthLargeHeader>
      <AuthMessage>
        {!emailMatch ? (
          <>
            You're currently logged in as {authState.email}. To accept this
            invite, please log out and log in again as{' '}
            {businessUserInvite.email}.
          </>
        ) : // assume a disallowed app client if we've got to here
        requireDefaultAppClient ? (
          <>
            Your organisation does not support this single sign-on provider.
            Please log out, and log in again using your email address and
            password
          </>
        ) : (
          <>
            Your organisation requires single sign-on. Please log out, and log
            in again using your single sign-on provider.
          </>
        )}
      </AuthMessage>

      <AuthButtons>
        <Button
          appearance="primary"
          size="large"
          onClick={async () => {
            await analytics.track(
              {
                event: 'Logged out of workbench',
                properties: {
                  category: 'general',
                  //@ts-ignore in reality this will be set, and if not sending undefined is ok
                  org_id: getLocalOrganisationId()
                }
              },
              true
            )
            // note that the redirect here was removed as cognito logout
            // redirects don't support wildcards (needed for the secret portion of the invite)
            auth.signOut()
          }}
        >
          Log out
        </Button>
      </AuthButtons>
    </>
  )
}

interface InviteSignupProps {
  businessUserInvite: BusinessUserInvite['attributes']
  switchToSignin: () => void
  onAuthComplete: AuthComplete
  auth: Authenticator
}
const InviteSignup = ({
  businessUserInvite,
  switchToSignin,
  onAuthComplete,
  auth
}: InviteSignupProps) => {
  return (
    <>
      <AuthLogo />
      <AuthLargeHeader>Accept invite</AuthLargeHeader>
      <AuthMessage>
        Sign up to accept your invite to {businessUserInvite.organisationName}
      </AuthMessage>

      <SignUpForm
        formOnly={true}
        auth={auth}
        email={businessUserInvite.email}
        inviteData={businessUserInvite.inviteData}
        onComplete={onAuthComplete}
      />

      <Button
        key="2"
        size="large"
        appearance="transparent"
        onClick={switchToSignin}
        style={{alignSelf: 'center'}}
      >
        Log in instead
      </Button>
    </>
  )
}

interface InviteSigninProps {
  businessUserInvite: BusinessUserInvite['attributes']
  switchToSignup: () => void
  onAuthComplete: AuthComplete
  auth: Authenticator
}
const InviteSignin = ({
  businessUserInvite,
  switchToSignup,
  onAuthComplete,
  auth
}: InviteSigninProps) => {
  const allowSSO = businessUserInvite.ssoEnabled
  const allowEmailPassword = businessUserInvite.allowDefaultAppClient

  return (
    <>
      <AuthLogo />
      <AuthLargeHeader>Accept invite</AuthLargeHeader>
      <AuthMessage>
        Log in to accept your invite to {businessUserInvite.organisationName}
      </AuthMessage>

      <SignInForm
        ssoOrgId={
          businessUserInvite.ssoEnabled
            ? businessUserInvite.organisationId
            : undefined
        }
        allowSSO={allowSSO}
        allowEmailPassword={allowEmailPassword}
        formOnly={true}
        auth={auth}
        email={businessUserInvite.email}
        onComplete={onAuthComplete}
      />
      {allowEmailPassword ? (
        <Button
          size="large"
          appearance="transparent"
          onClick={switchToSignup}
          style={{alignSelf: 'center'}}
        >
          Sign up instead
        </Button>
      ) : null}
    </>
  )
}

const InviteError = () => (
  <>
    <AuthLogo />
    <AuthLargeHeader>Invite error</AuthLargeHeader>
    <AuthMessage>Invite link is invalid or expired</AuthMessage>
  </>
)
